<template>
    <page-container data-test="segment-list">
        <page-header>
            {{ props.templateView ? 'Segment Templates' : 'Segments' }}
            <template v-if="!props.withoutOverview" v-slot:actions>
                <vx-button
                    @click="showOverview = !showOverview"
                    data-test="toggle-overview"
                    type="button"
                    color="muted-dark"
                    size="lg"
                >
                    <span class="hidden sm:inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                    <font-awesome-icon
                        widthAuto
                        class="hidden! sm:inline-block!"
                        :icon="faCircleQuestion"
                    ></font-awesome-icon>
                    <font-awesome-icon
                        widthAuto
                        class="inline-block! sm:hidden!"
                        :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"
                    ></font-awesome-icon>
                </vx-button>
            </template>
        </page-header>

        <segments-overview
            v-if="!props.withoutOverview"
            v-show="showOverview"
            class="mb-14"
            :orgUnitId="props.orgUnitId"
            :groupId="props.groupId"
            :templateView="props.templateView"
        ></segments-overview>

        <segment-list-filters
            v-if="totalCount > 0 || !isEqual(defaultFilters(), fetchParams)"
            v-model="fetchParams"
            @reset="resetFilters"
            @apply="applyFilters"
            :orgUnitId="props.orgUnitId"
            :groupId="props.groupId"
            :readOnly="props.readOnly"
            :templateView="props.templateView"
        ></segment-list-filters>

        <template v-if="!loading && !list.length">
            <vx-box
                color="info"
                v-if="totalCount === 0 && !isEqual(defaultFilters(), fetchParams)"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                <div class="w-64 mx-auto text-center">No results for current search, try adjusting your filters.</div>
                <vx-button type="button" color="secondary" @click="resetFilters">
                    Reset All <font-awesome-icon widthAuto :icon="faCircleX" />
                </vx-button>
            </vx-box>
            <vx-box
                color="info"
                v-else-if="totalCount === 0"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                No {{ props.templateView ? 'templates' : 'segments' }} yet.
                <vx-button
                    data-test="add-button"
                    v-if="!props.readOnly"
                    :href="props.templateView ? '/contacts/segments/templates/new' : '/contacts/segments/new'"
                >
                    New {{ props.templateView ? 'Template' : 'Segment' }}
                    <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </vx-box>
        </template>

        <vx-table v-if="list.length" rowBreakpoint="md" :class="tableClasses">
            <vx-table-row isHeader>
                <div class="hidden lg:block">Name</div>
                <div v-if="!props.templateView">Contacts</div>
                <div>Audience <span class="hidden lg:inline-block">Type</span></div>
                <div v-if="props.templateView">Groups</div>
                <div v-if="props.templateView">Updated</div>
                <div v-if="props.templateView || props.groupId">Preferred Group</div>
                <div v-if="!props.templateView && !props.groupId && !props.orgUnitId">Refresh Type</div>
                <div v-if="!props.templateView && !props.groupId && !props.orgUnitId">Last Refreshed</div>
                <div v-if="FEATURES.flows && !props.templateView && !props.groupId && !props.orgUnitId">Flow</div>
            </vx-table-row>

            <segment-row
                v-for="(segment, index) in list"
                :templateView="props.templateView"
                :segment="list[index]"
                :key="list[index].id"
                @deleted="removeFromList($event)"
                :groupId="props.groupId"
                :orgUnitId="props.orgUnitId"
                :readOnly="props.readOnly"
            ></segment-row>
        </vx-table>

        <div v-if="list.length" class="mt-8 flex justify-between w-full gap-10">
            <vx-button
                v-if="fetchParams.cursor"
                @click="loadData()"
                color="muted-dark"
                size="xl"
                class="grow-0"
                data-test="load-more-btn"
                :loading="loading"
            >
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
            </vx-button>
            <div class="text-lg grow-0 ml-auto font-normal text-slate-600">{{ list.length }}/{{ totalCount }}</div>
        </div>
    </page-container>
</template>
<script setup>
import PageContainer from '~/components/general/PageContainer.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { VxButton, VxBox, VxTable, VxTableRow } from '@voxie/frontend-components';
import {
    faCircleX,
    faCircleQuestion,
    faCirclePlus,
    faSpinner,
    faCircleChevronDown,
    faCircleChevronUp,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import SegmentsOverview from './SegmentsOverview.vue';
import SegmentListFilters from './SegmentListFilters.vue';
import SegmentRow from './SegmentRow.vue';
import { ref, onMounted, inject, computed } from 'vue';
import { segmentsClient } from '~/services';
import { useToasted } from '../../../composables/useToasted';
import { isEqual, isAuthErrorEcho } from '~/utils/helpers';
import { useCursorPagination } from '../../../composables/useCursorPagination';
import { useSegmentsContactsCount } from '~/composables/useSegmentsContactsCount';
import { useSegmentsGroupsCount } from '~/composables/useSegmentsGroupsCount';

const FEATURES = inject('FEATURES');

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    withoutOverview: {
        type: Boolean,
        default: false,
    },
    templateView: {
        type: Boolean,
        default: false,
    },
});

const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();

const showOverview = ref(true);

const { fetchSegmentsContactsCount } = useSegmentsContactsCount();
const { fetchSegmentsGroupsCount } = useSegmentsGroupsCount();
const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const defaultFilters = () => {
    const defaultFilters = {
        query: '',
        per_page: 10,
        cursor: null,
        'filter[audience_type]': undefined,
        'filter[refresh_type]': undefined,
        'filter[category]': undefined,
        'filter[custom_attribute_key]': undefined,
        sort_field: 'id',
        sort_dir: 'desc',
    };

    if (!props.groupId) {
        defaultFilters['filter[is_template]'] = props.templateView ? 1 : 0;
    }

    if (FEATURES.flows) {
        defaultFilters['filter[flow]'] = {
            in_flow: undefined,
            flow_id: null,
        };
    }

    return defaultFilters;
};

const totalCount = ref(0);

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

onMounted(() => {
    resetFilters();
});

const fetchTotalCount = async () => {
    try {
        const response = await segmentsClient.getSegmentsCount(
            { teamId, orgUnitId: props.orgUnitId, groupId: props.groupId },
            { ...fetchParams.value }
        );

        totalCount.value = response.data?.count || 0;
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    }
};

const loadData = () => {
    const request = segmentsClient.getSegments(
        { teamId, orgUnitId: props.orgUnitId, groupId: props.groupId },
        { ...fetchParams.value }
    );

    fetchRecords(request).then((data) => {
        if (data?.length) {
            const segmentIds = data.map((segment) => segment.id);
            fetchSegmentsContactsCount({ teamId, orgUnitId: props.orgUnitId, groupId: props.groupId }, segmentIds);

            if (props.templateView) {
                fetchSegmentsGroupsCount(segmentIds);
            }
        }
    });
    fetchTotalCount();
};

const removeFromList = (segmentId) => {
    list.value = list.value.filter((item) => item.id != segmentId);
    totalCount.value = totalCount.value - 1;

    if (!list.value.length) {
        loadData();
    }
};

const updateSegment = (updatedSegment) => {
    list.value = list.value.map((segment) => {
        if (segment.id == updatedSegment.id) {
            return {
                ...segment,
                ...updatedSegment,
            };
        }

        return segment;
    });
};

const echoErrors = ref([]);
Echo.private(`segments.${teamId}`)
    .listen('ContactSegmentationComplete', (event) => {
        updateSegment(event.segment);
    })
    .listen('ContactSegmentationTriggered', (event) => {
        updateSegment(event.segment);
    })
    .error?.((e) => {
        if (echoErrors.value.length <= 3 && !isAuthErrorEcho(e)) {
            window?.Bugsnag?.notify(e instanceof Error ? e : new Error(JSON.stringify(e)));
            echoErrors.value.push(e);
        }
    });

const tableClasses = computed(() => {
    if (props.orgUnitId) {
        return 'md:grid-cols-3-auto lg:grid-cols-4-auto';
    } else if (props.groupId) {
        return 'md:grid-cols-4-auto lg:grid-cols-5-auto';
    }

    if (!props.templateView && FEATURES.flows) {
        return 'md:grid-cols-6-auto lg:grid-cols-7-auto';
    }

    return 'md:grid-cols-5-auto lg:grid-cols-6-auto';
});
</script>
